/* eslint-disable max-lines */
import Button from '@components/atoms/ui-kit/button.tsx';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import { DoubleArrowLeft } from '@components/shared/icons.tsx';
import PageHeader from '@components/layout/page-header.tsx';
import { useCancelSubscription } from '@utils/api/subscriptions/use-delete-subscription.tsx';
import { useGetSubscription } from '@utils/api/subscriptions/use-get-subscription.tsx';
import { formatCustomModelYearlyUsage } from '@utils/helpers/custom-model-usage.ts';
import {
  UsageDetail,
  useGetUsage
} from '@utils/api/subscriptions/use-get-usages.tsx';
import { formatDate } from '@utils/date.ts';
import { useRoles } from '@utils/hooks/use-roles.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { isAxiosError } from 'axios';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PATHS } from '../../router/paths.ts';
import { useOrganization } from '@utils/api/users/use-get-organization.tsx';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2.tsx';
import { Card } from '@components/atoms/ui-kit/card.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';

export const SubscriptionsDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { subscriptionId } = useParams<{ subscriptionId: string }>();

  const [isCancelSubscriptionModalOpen, setIsCancelSubscriptionModalOpen] =
    useState(false);
  const [cancelingSubscription, setCancelingSubscription] = useState(false);

  const { data } = useGetSubscription(subscriptionId);
  const { isReseller } = useRoles();

  const {
    data: usageData,
    isLoading: usageIsLoading,
    isFetching: usageIsFetching
  } = useGetUsage(subscriptionId);

  const { mutateAsync: cancelSubscription } = useCancelSubscription();
  const { data: organization } = useOrganization();

  const safeFormatDate = (d?: string | null) =>
    d && !Number.isNaN(new Date(d).getTime()) ? formatDate(d) : '-';

  const { statusText, canCancel, hasCancellationScheduled } = useMemo(() => {
    const today = new Date();
    const raw = data?.result;
    const suspended = Boolean(raw?.suspended);

    const cancelDate = raw?.cancel_date ? new Date(raw.cancel_date) : null;
    const endDate = raw?.end_date ? new Date(raw.end_date) : null;

    const endedByEndDate = !!endDate && endDate < today;
    const cancellationScheduled = !!cancelDate && cancelDate > today;
    const cancellationEffective = !!cancelDate && cancelDate <= today;

    let status = t('active');
    if (suspended) status = t('suspended');
    else if (cancellationEffective) status = t('ended');
    else if (cancellationScheduled)
      status = `${t('active')} (${t('cancels-on')} ${safeFormatDate(raw?.cancel_date)})`;
    else if (endedByEndDate) status = t('ended');

    const canCancelNow =
      !suspended &&
      !cancellationScheduled &&
      !cancellationEffective &&
      !endedByEndDate;

    return {
      statusText: status,
      canCancel: canCancelNow,
      hasCancellationScheduled: cancellationScheduled || cancellationEffective
    };
  }, [data, t]);

  const columns = useMemo<DataGridColumn<UsageDetail>[]>(
    () => [
      {
        id: 'period_start',
        accessorKey: 'period_start',
        header: t('period-start'),
        minWidth: 180,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '-')
      },
      {
        id: 'period_end',
        accessorKey: 'period_end',
        header: t('period-end'),
        minWidth: 180,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '-')
      },
      {
        id: 'stt_units_used',
        accessorKey: 'stt_units_used',
        header: t('stt-units-used'),
        minWidth: 180,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '-')
      },
      {
        id: 'alignment_units_used',
        accessorKey: 'alignment_units_used',
        header: t('alignment-units-used'),
        minWidth: 180,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '-')
      },
      {
        id: 'translation_characters_used',
        accessorKey: 'translation_characters_used',
        header: t('translation-characters-used'),
        minWidth: 180,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '-')
      },
      {
        id: 'custom_models_trained',
        accessorKey: 'custom_models_trained',
        header: t('custom-models-trained'),
        minWidth: 180,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '-')
      },
      {
        id: 'custom_models_hosted',
        accessorKey: 'custom_models_hosted',
        header: t('custom-models-hosted'),
        minWidth: 180,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '-')
      },
      {
        id: 'billed',
        accessorKey: 'billed',
        header: t('billed'),
        minWidth: 80,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => (getValue() ? t('yes') : t('no'))
      }
    ],
    [t]
  );

  const rows: UsageDetail[] = useMemo(() => {
    if (!usageData) return [];
    return usageData.result.map(u => ({
      ...u,
      period_start: safeFormatDate(u.period_start),
      period_end: safeFormatDate(u.period_end)
    }));
  }, [usageData]);

  const handleCancelSubscription = (id: number) => {
    setCancelingSubscription(true);
    cancelSubscription(id)
      .then(response => {
        if (response.success)
          toast.success(t('subscription-cancelled-successfully'));
        else toast.error(response.message || t('an-error-has-occurred'));
      })
      .catch(err => {
        let message = t('an-error-has-occurred');
        if (isAxiosError<BaseError>(err)) {
          const d = err.response?.data;
          message =
            d?.error_description ||
            d?.detail ||
            d?.error ||
            d?.message ||
            message;
        }
        toast.error(message);
      })
      .finally(() => {
        setCancelingSubscription(false);
        setIsCancelSubscriptionModalOpen(false);
      });
  };

  const sub = data?.result;

  return (
    <div className="bg-app min-h-screen">
      <PageHeader
        title={`${t('subscription')} #${subscriptionId} ${t('status')} - ${statusText}`}
        caption=""
      >
        <Button
          onClick={() => navigate(PATHS().BILLING_AND_SUBSCRIPTION)}
          size="medium"
          variant="smoke"
          className="mr-2"
          icon={<DoubleArrowLeft width={14} />}
          label={t('subscriptions')}
        />
        {isReseller && (
          <Button
            variant="secondary"
            onClick={() => setIsCancelSubscriptionModalOpen(true)}
            label={t('cancel')}
            size="medium"
            disabled={!canCancel}
            title={
              !canCancel
                ? hasCancellationScheduled
                  ? t('cancellation-already-scheduled')
                  : sub?.suspended
                    ? t('subscription-is-suspended')
                    : t('subscription-not-cancellable')
                : undefined
            }
          />
        )}
        {isReseller && canCancel && sub && (
          <ConfirmationModal
            isOpen={isCancelSubscriptionModalOpen}
            onClose={() => setIsCancelSubscriptionModalOpen(false)}
            onConfirm={() => handleCancelSubscription(sub.id)}
            size="small"
            title={t('cancel-subscription')}
            caption={t('cancel-subscription-confirmation')}
            confirmLabel={cancelingSubscription ? t('canceling') : t('confirm')}
            cancelLabel={t('cancel')}
            confirmDisabled={cancelingSubscription}
            destructive
            autoFocusConfirm
          />
        )}
      </PageHeader>

      <Card padding="sm" className="mx-4 sm:mx-0 my-4">
        <dl className="grid grid-cols-2 md:grid-cols-5 gap-x-4">
          <div className="py-2 border-b border-hairline">
            <dt>
              <Text as="span" size="sm">
                {t('subscription-plan')}
              </Text>
            </dt>
            <dd className="mt-1 leading-6">
              <Text as="span" size="sm" weight="semibold" tone="brand">
                {sub?.sku}
              </Text>
            </dd>
          </div>
          <div className="py-2 border-b border-hairline">
            <dt>
              <Text as="span" size="sm">
                {t('start-date')}
              </Text>
            </dt>
            <dd className="mt-1 leading-6">
              <Text as="span" size="sm" weight="semibold">
                {safeFormatDate(sub?.start_date)}
              </Text>
            </dd>
          </div>
          <div className="py-2 border-b border-hairline">
            <dt>
              <Text as="span" size="sm">
                {t('end-date')}
              </Text>
            </dt>
            <dd className="mt-1">
              <Text as="span" size="sm" weight="semibold">
                {safeFormatDate(sub?.end_date)}
              </Text>
            </dd>
          </div>
          {organization && organization.is_reseller && (
            <div className="py-2 border-b border-hairline">
              <dt>
                <Text as="span" size="sm">
                  {t('available-credits')}
                </Text>
              </dt>
              <dd className="mt-1 leading-6">
                <Text as="span" size="sm" weight="semibold">
                  {sub?.available_stt_budget}
                </Text>
              </dd>
            </div>
          )}
          <div className="py-2 border-b border-hairline">
            <dt>
              <Text as="span" size="sm">
                {t('speech-to-text')}
              </Text>
            </dt>
            <dd className="mt-1 leading-6">
              <Text as="span" size="sm" weight="semibold">
                {sub?.available_stt_units}
              </Text>
            </dd>
          </div>
          <div className="py-2 border-b border-hairline">
            <dt>
              <Text as="span" size="sm">
                {t('alignment')}
              </Text>
            </dt>
            <dd className="mt-1 leading-6">
              <Text as="span" size="sm" weight="semibold">
                {sub?.available_alignment_units}
              </Text>
            </dd>
          </div>
          <div className="py-2 border-b border-hairline">
            <dt>
              <Text as="span" size="sm">
                {t('translation')}
              </Text>
            </dt>
            <dd className="mt-1 leading-6">
              <Text as="span" size="sm" weight="semibold">
                {sub?.available_translation_budget}
              </Text>
            </dd>
          </div>
          <div className="py-2 border-b border-hairline">
            <dt>
              <Text as="span" size="sm">
                {t('custom-models-this-year')}
              </Text>
            </dt>
            <dd className="mt-1 leading-6">
              <Text as="span" size="sm" weight="semibold">
                {formatCustomModelYearlyUsage(
                  sub?.custom_model_yearly_used,
                  sub?.custom_model_yearly_limit,
                  t('unlimited')
                )}
              </Text>
            </dd>
          </div>
          <div className="py-2 border-b border-hairline">
            <dt>
              <Text as="span" size="sm">
                {t('auto-renew')}
              </Text>
            </dt>
            <dd className="mt-1 leading-6">
              <Text as="span" size="sm" weight="semibold">
                {sub?.auto_renew ? t('yes') : t('no')}
              </Text>
            </dd>
          </div>
          <div className="py-2 border-b border-hairline">
            <dt>
              <Text as="span" size="sm">
                {t('billing-channel')}
              </Text>
            </dt>
            <dd className="mt-1 leading-6">
              <Text as="span" size="sm" weight="semibold">
                {sub?.billing_channel}
              </Text>
            </dd>
          </div>
          <div className="py-2 border-b border-hairline">
            <dt>
              <Text as="span" size="sm">
                {t('cancellation-date')}
              </Text>
            </dt>
            <dd className="mt-1 leading-6">
              <Text as="span" size="sm" weight="semibold">
                {safeFormatDate(sub?.cancel_date)}
              </Text>
            </dd>
          </div>
        </dl>
      </Card>

      {/* Usage table */}
      <div className="mt-4 px-4 sm:px-0 pb-4 sm:pb-0">
        <div className="w-full space-y-4">
          <PageHeader title={t('usage-details')} caption={t('')} />
          {usageIsLoading ? (
            <Text as="p" size="sm" tone="muted" className="text-center">
              {t('loading-usages')}...
            </Text>
          ) : (
            <DataGridV2<UsageDetail>
              data={rows}
              columns={columns}
              enableRowSelection={false}
              enablePagination
              manualPagination
              enableSorting
              manualSorting
              enableFiltering
              manualFiltering
              totalItems={usageData ? usageData.total_results : undefined}
              pageSize={25}
              pageSizeOptions={[25, 50, 75, 100]}
              showColumnFilters={false}
              filterMode="popover"
              height="58vh"
              dense
              grid={false}
              striped
              loading={usageIsLoading || usageIsFetching || !usageData}
              getRowId={row => String(row.id)}
            />
          )}
        </div>
      </div>
    </div>
  );
};
